import { Survey } from "survey-react-ui";
import MDBox from "../../components/MDBox";
import { Model } from "survey-core";
import surveyTheme from "../survey_theme.json";
import surveyService from "../../services/survey-service";
import { useEffect, useState } from "react";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import MDTypography from "../../components/MDTypography";
import MDButton from "../../components/MDButton";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";

export default function SurveyComponent({
	surveyId,
	responseId,
	isEditMode = false,
	handleBackToList,
	viewOnly = false,
}) {
	const [loading, setLoading] = useState(false);
	const [surveyModel, setSurveyModel] = useState(null);
	const [existingResponse, setExistingResponse] = useState(null);
	const navigate = useNavigate();
	const location = useLocation();

	// Parse URL parameters to determine mode
	const urlParams = new URLSearchParams(location.search);
	const urlResponseId = urlParams.get("id");
	const isViewOnly = urlParams.get("viewOnly") === "true" || viewOnly;
	const isEdit = urlParams.get("edit") === "true" || isEditMode;

	// Use URL parameters if provided, otherwise use props
	const actualResponseId = urlResponseId || responseId;
	const actualIsEditMode = isEdit && !isViewOnly;
	const actualViewOnly = isViewOnly;

	useEffect(() => {
		console.log("Survey useEffect triggered with:", {
			surveyId,
			actualResponseId,
			actualIsEditMode,
			actualViewOnly,
		});

		if (surveyId) {
			loadSurvey(surveyId, actualResponseId);
		} else {
			setLoading(false);
		}
	}, [surveyId, actualResponseId, actualIsEditMode, actualViewOnly]);

	const loadSurvey = async (surveyId, responseId) => {
		console.log("loadSurvey called with:", { surveyId, responseId });
		try {
			setLoading(true);

			const response = await surveyService.getSurveyById(surveyId);
			const surveyJson = response.data[0].json_data.jsonField;

			const surveyModel = new Model(surveyJson);
			surveyModel.applyTheme(surveyTheme);
			surveyModel.locale = "it";

			// Configure read-only mode if viewing only
			if (actualViewOnly) {
				surveyModel.mode = "display";
				surveyModel.showCompletedPage = false;
			}

			if (responseId) {
				const response =
					await surveyService.getSurveyResponseById(responseId);
				setExistingResponse(response.data);
				surveyModel.data = response.data.json_data;
			}

			// Only add completion handler if not in view-only mode
			if (!actualViewOnly) {
				surveyModel.onComplete.add(async (sender) => {
					surveyModel.showCompletedPage = false;
					try {
						if (responseId) {
							// If we have a responseId, we're editing an existing response
							await surveyService.updateSurveyResponse(
								responseId,
								sender.data
							);
							toast.success("Risposta aggiornata con successo!");
							setTimeout(() => {
								navigate("/survey-runner/list");
							}, 1000);
						} else {
							// Creating a new response
							await surveyService.saveSurveyResponse(
								parseInt(surveyId),
								sender.data
							);
							toast.success("Risposta salvata con successo!");
							setTimeout(() => {
								navigate("/survey-creator/list");
							}, 1000);
						}
					} catch (error) {
						console.error("Error saving survey response:", error);
						toast.error("Errore nel salvataggio della risposta");
					}
				});
			}

			console.log("Survey model created successfully");
			setSurveyModel(surveyModel);
		} catch (error) {
			console.error("Error loading survey:", error);
			toast.error("Errore nel caricamento del sondaggio");
		} finally {
			setLoading(false);
		}
	};

	if (loading) {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<MDBox py={3} display="flex" justifyContent="center">
					<MDTypography variant="h6">
						{actualViewOnly
							? "Caricamento sondaggio da visualizzare..."
							: actualIsEditMode
								? "Caricamento sondaggio da modificare..."
								: "Caricamento sondaggio..."}
					</MDTypography>
				</MDBox>
			</DashboardLayout>
		);
	}

	if (!surveyModel) {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<MDBox py={3} textAlign="center">
					<MDTypography variant="h5" color="error" mb={2}>
						Sondaggio non trovato
					</MDTypography>
					<MDButton
						variant="outlined"
						color="secondary"
						onClick={handleBackToList}
					>
						Torna alla Lista
					</MDButton>
				</MDBox>
			</DashboardLayout>
		);
	}

	return (
		<>
			{actualViewOnly && (
				<MDBox
					mb={2}
					p={2}
					sx={{ backgroundColor: "#f3e5f5", borderRadius: 1 }}
				>
					<MDTypography variant="body2" color="primary">
						<strong>Modalità Visualizzazione:</strong> Stai
						visualizzando una risposta compilata. Il sondaggio è in
						sola lettura.
					</MDTypography>
					<MDBox mt={1}>
						<MDButton
							variant="outlined"
							color="secondary"
							size="small"
							onClick={() => navigate("/survey-runner/list")}
						>
							← Torna alla Lista
						</MDButton>
					</MDBox>
				</MDBox>
			)}
			{actualIsEditMode && !actualViewOnly && (
				<MDBox
					mb={2}
					p={2}
					sx={{ backgroundColor: "#e3f2fd", borderRadius: 1 }}
				>
					<MDTypography variant="body2" color="info">
						<strong>Modalità Modifica:</strong> Stai modificando una
						risposta esistente. Le risposte precedenti sono già
						caricate nel modulo.
					</MDTypography>
				</MDBox>
			)}
			<MDBox
				sx={{
					backgroundColor: "white",
					borderRadius: 2,
					p: 3,
					minHeight: "400px",
				}}
			>
				<Survey model={surveyModel} />
			</MDBox>
		</>
	);
}
